// Core packages
import {
  Component, Inject,
  OnDestroy, OnInit
} from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { PlatformNotesService } from "../../platformNotes.service";

@Component({
  selector: "app-edit-platform-note-dialog",
  templateUrl: "./edit-platform-note-dialog.component.html",
  styleUrls: ["./edit-platform-note-dialog.component.scss"]
})
export class EditPlatformNoteDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  user: User = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Add subUser form

  dialogIsLoading = true;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<EditPlatformNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,

    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private platformNotesService: PlatformNotesService,
    private authService: AuthService,
    private helperService: HelperService,
    private apiService: ApiService
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init add form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = this.data.item;
    
    // Set fields
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-4",
            type: "select",
            key: "category",
            templateOptions: {
              label: this.translateService.instant(
                "platformNotes.fields.category"
              ),
              placeholder: this.translateService.instant(
                "platformNotes.fields.category"
              ),
              required: true,
              options: [
                {
                  label: this.translateService.instant("platformNotes.categories.subscription"),
                  value: "subscription"
                },
                {
                  label: this.translateService.instant("platformNotes.categories.work-account"),
                  value: "work-account"
                },
                {
                  label: this.translateService.instant("platformNotes.categories.technical-operational"),
                  value: "technical-operational"
                },
                {
                  label: this.translateService.instant("platformNotes.categories.insertion"),
                  value: "insertion"
                },
                {
                  label: this.translateService.instant("platformNotes.categories.mapping"),
                  value: "mapping"
                },
                {
                  label: this.translateService.instant("platformNotes.categories.packaging"),
                  value: "packaging"
                },
                {
                  label: this.translateService.instant("platformNotes.categories.tagnfc"),
                  value: "tagnfc"
                },
                {
                  label: this.translateService.instant("platformNotes.categories.suppliers"),
                  value: "suppliers"
                },
                {
                  label: this.translateService.instant("platformNotes.categories.other"),
                  value: "other"
                },
              ]
            },
          },
          {
            className: "col-12 col-md-8",
            type: "input",
            key: "title",
            templateOptions: {
              label: this.translateService.instant(
                "platformNotes.fields.title"
              ),
              placeholder: this.translateService.instant(
                "platformNotes.fields.title"
              ),
              required: true,
              minLength: 0,
              maxLength: 1000,
            }
          },
          {
            className: "col-12",
            type: "textarea",
            key: "description",
            templateOptions: {
              label: this.translateService.instant(
                "platformNotes.fields.description"
              ),
              placeholder: this.translateService.instant(
                "platformNotes.fields.description"
              ),
              required: true,
              minLength: 0,
              maxLength: 20000,
              rows: 4,
            }
          },
          {
            className: "col-12",
            type: "file-uploader",
            key: "file",
            templateOptions: {
              required: false,
              allowedFilesNumber: 1,
              allowedFilesExtensions: `${this.configService.allowedDocumentExtensions},xls,xlsx`,
              customLabel: this.translateService.instant('platformNotes.fields.file'),
            },
          }
          
        ]
      }
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      console.warn('form', this.form);
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    this.formBtnOptions.active = true;

    // Create subUser for company
    this.subscriptions.push(
      this.platformNotesService
        .updateNote(this.data.item._id, this.model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.onClose({ status: true });
              return;
            }
            this.toastrService.error(
              this.translateService.instant(
                "common.errors.unknown_server_error"
              )
            );
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

}
